import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface UseSignUpProps {
  successPath?: string;
  onSuccess?: () => void;
}

interface SignUpError {
  message?: string;
  response?: {
    status?: number;
    data?: {
      message?: string;
      error?: string;
      reason?: string;
      detail?: string;
    };
  };
}

export const useSignUp = ({ successPath = '/verification-needed', onSuccess }: UseSignUpProps = {}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [coupon, setCoupon] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();

  const validatePassword = (): boolean => {
    if (password === confirmPassword) {
      return true;
    } else {
      setError('Passwords do not match!');
      return false;
    }
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    if (!validatePassword()) {
      setIsSubmitting(false);
      return;
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Invite-Token': inviteToken,
      }
    };

    try {
      const response = await axios.post('signupUser', {
        name,
        email,
        password
      }, config);

      const data = response.data;
      if (data.userId) {
        const trimmedCoupon = coupon.trim();
        if (trimmedCoupon) {
          sessionStorage.setItem('pending_coupon', trimmedCoupon);
        }
        if (onSuccess) {
          onSuccess();
        }

        if (data.redirectPath) {
          window.location.assign(data.redirectPath);
          return;
        }

        navigate(`${successPath}?email=${encodeURIComponent(email)}`, {
          state: { "createdAccountMessage": "Account created successfully, please check your email for a verification link" }
        });
      } else {
        setError("Signup failed");
      }
    } catch (error) {
      const err = error as SignUpError;
      console.error("An error occurred during signup:", err);
      
      if (err.response?.status === 409) {
        setError("Email already exists, please contact us to reset password at support@uitesting.tools");
      } else {
        // Extract error message from various possible locations in the response
        const errorMessage = 
          err.response?.data?.message || 
          err.response?.data?.error || 
          err.response?.data?.reason || 
          err.response?.data?.detail ||
          err.message ||
          "An error occurred during signup";
        
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    name,
    setName,
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    inviteToken,
    setInviteToken,
    coupon,
    setCoupon,
    isSubmitting,
    error,
    setError,
    handleSubmit
  };
};
